import { memo, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { AdForm } from '../form';

import css from './AdCard.module.css';

import { adsService } from 'v2/api/adsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { AdModel } from 'v2/types/AdModel';
import { handleHttpError } from 'v2/utils';

interface AdCardProps {
  ad: AdModel;
  onRevalidate: VoidFunction;
}

export const AdCard = memo<AdCardProps>(({ ad, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(['updateAd', ad.id], adsService.updateAd, {
    onSuccess: () => {
      toast.success('The ad has been updated successfully');
      onRevalidate();
      handleClose();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerDelete } = useSWRMutation(['deleteAd', ad.id], adsService.deleteAd, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<AdForm>({
    initialValues: { key: ad.key },
    onSubmit: (values) => {
      triggerUpdate(values);
    },
  });

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <div className={css.adCard}>
      <p className="truncate">{ad.key}</p>

      <div className={css.adCardActions}>
        <Tooltip title="Edit" placement="top">
          <IconButton onClick={setOpen} color="warning">
            <SquarePen width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Are you sure that you want to delete this ad?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Ad</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              name="key"
              value={formik.values.key}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Key"
              required
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button loading={isMutating} type="submit" variant="contained">
              Edit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
});

AdCard.displayName = 'AdCard';
